import {
  Box,
  Button,
  classSet,
  Modal
} from '@platform/ui-library';
import {
  FC,
  SyntheticEvent,
  useEffect,
  useRef,
  useState
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { Select } from '@app/form_components';
import { useCustomNavigate, useServices } from '@app/hooks';
import { Option } from '@app/types';

type FormValues = {
  branchId?: string | null;
  clientId?: string | null;
  designId?: string | null;
  version?: string | null;
};

export const HomeDownloadPage: FC = () => {
  const searchDesignId = useSearchParams()[0].get('designId');
  const navigate = useCustomNavigate();
  const {
    avsService,
    errorMessageService,
    lokiService
  } = useServices();
  const [branches, setBranches] = useState<Option<string>[] | null>(null);
  const [clients, setClients] = useState<Option<string>[] | null>(null);
  const [designs, setDesigns] = useState<Option<string>[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [formValues, setFormValuesState] = useState<FormValues>({});
  const [latestVersionOption, setLatestVersionOption] = useState<Option<string> | null>(null);
  const [versions, setVersions] = useState<Option<string>[] | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    contentRef.current?.querySelector('input')?.focus();
    (async () => {
      if (!searchDesignId) {
        setClients(Option.fromIdAndName(await avsService.getClients()));
        return;
      }

      const designInfo = await avsService.getDesignInfo(searchDesignId);
      if (!designInfo) {
        errorMessageService.addError(`Invalid design ID ${searchDesignId}`);
        return;
      }
      const {
        client: { id: clientId },
        id: designId
      } = designInfo;
      setFormValuesState({ clientId });
      setFormValues({ designId });
    })();
  }, []);

  const setFormValues = async ({
    branchId,
    clientId,
    designId,
    version
  }: FormValues) => {
    if (version) {
      setFormValuesState((formValues) => {
        return { ...formValues, version };
      });
      return;
    }
    setLatestVersionOption(null);
    setVersions(null);
    if (branchId) {
      setFormValuesState((formValues) => {
        return {
          ...formValues,
          branchId,
          version: null
        };
      });
      const latestVersion = (await avsService.getLatestVersion(branchId))?.version;
      if (latestVersion) {
        setLatestVersionOption({ label: latestVersion, value: latestVersion });
        setFormValues({ version: latestVersion });
      }

      const options = Option.fromStrings(
        (await avsService.getVersions(branchId)).map(({ version }) => version)
      );
      setVersions(options);
      if (options[0]?.value && options[0].value !== latestVersion) {
        setFormValues({
          version: options[0].value
        });
      }
      return;
    }
    setBranches(null);
    if (designId) {
      setFormValuesState((formValues) => {
        return {
          ...formValues,
          designId
        };
      });
      const options = Option.fromIdAndName(
        await avsService.getBranches(designId)
      );
      setBranches(options);
      setFormValues({
        branchId: options.find(({ label }) => {
          return label === avsService.mainBranchName;
        })?.value
      });
      return;
    }
    setDesigns(null);
    if (clientId) {
      setFormValuesState({ clientId });
      const options: Option<string>[] = Option.fromIdAndName(
        await avsService.getDesigns(clientId)
      );
      setDesigns(options);
      if (options.length === 1) {
        setFormValues({ designId: options[0].value });
      }
    }
  };

  const onDownload = async (event: SyntheticEvent) => {
    event.preventDefault();
    const { branchId, version } = formValues;
    if (isLoading) {
      return;
    } else if (!branchId || !version) {
      errorMessageService.addError('Please fill in all the fields prior to submission.');
      return;
    }
    setIsLoading(true);
    try {
      const serialized = await avsService.getDesign(branchId, version);
      if (await lokiService.storeSerialized(serialized)) {
        navigate('..');
      }
    } catch (error) {
      errorMessageService.addError(error as Error);
    }
    setIsLoading(false);
  };

  return (
    <Modal className="overflow-visible" isOpen>
      <form onSubmit={onDownload}>
        <Box theme="noPadding">
          <div className="box header">
            <h2>Download Version</h2>
          </div>
          <div className="box content overflow-visible" ref={contentRef}>
            {(searchDesignId) ? null : (
              <>
                <Select
                  isLoading={!clients}
                  label="Client"
                  name="client"
                  options={clients}
                  onChange={(clientId) => {
                    setFormValues({ clientId });
                  }}
                  value={formValues.clientId}
                />
                <Select
                  isLoading={!!formValues.clientId && !designs}
                  label="Design"
                  name="design"
                  noOptionsMessage={() => {
                    return (formValues.clientId) ? 'No options.' : 'Select a client first.';
                  }}
                  options={designs}
                  onChange={(designId) => {
                    setFormValues({ designId });
                  }}
                  value={formValues.designId}
                />
              </>
            )}
            <div className={classSet(['flex-gap', searchDesignId ? 'flex-column' : 'flex-wrap'])}>
              <Select
                fieldConfig={{
                  className: 'flex-1',
                  size: 'small'
                }}
                isLoading={!!formValues.designId && !branches}
                label="Branch"
                name="branch"
                options={branches}
                onChange={(branchId) => {
                  setFormValues({ branchId });
                }}
                value={formValues.branchId}
              />
              <Select
                fieldConfig={{
                  className: 'flex-1',
                  size: 'small'
                }}
                isLoading={!!formValues.branchId && !versions}
                label="Version"
                name="version"
                options={versions ?? ((latestVersionOption) ? [latestVersionOption] : null)}
                onChange={(version) => {
                  setFormValues({ version: version });
                }}
                value={formValues.version}
              />
            </div>
          </div>
          <div className="box footer no-border">
            <Button
              onClick={() => {
                navigate('..');
              }}
              theme="outline"
              type="button"
            >
              {window.l.popup.cancel}
            </Button>
            <Button theme="default">
              {(isLoading) ? 'Loading...' : 'Download'}
            </Button>
          </div>
        </Box>
      </form>
    </Modal>
  );
};
